<template>
  <div class="timesheet-submit-period">
    <CCard>
      <CCardBody>
        <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
          <CForm @submit.prevent="handleSubmit(submit)">
            <p v-if="current.submit_start !== ''" class="description">
              {{ $t("timesheet_submit_period.description", { start: current.submit_start, end: current.submit_end }) }}
            </p>
            <CRow>
              <CCol md="3" class="form-field">
                <label>{{ $t("timesheet_submit_period.submit_start") }} <span class="require">*</span></label>
                <ValidationProvider :rules="dayRules" v-slot="{ errors }" :name="$t('timesheet_submit_period.submit_start')">
                  <b-form-input v-model.number="form.submit_start" type="number" :min="minDay" :max="maxDay" :disabled="!canUpdate" />
                  <span class="require">{{ errors[0] || orderError || apiErrors.submit_start }}</span>
                </ValidationProvider>
              </CCol>
              <CCol md="3" class="form-field">
                <label>{{ $t("timesheet_submit_period.submit_end") }} <span class="require">*</span></label>
                <ValidationProvider :rules="dayRules" v-slot="{ errors }" :name="$t('timesheet_submit_period.submit_end')">
                  <b-form-input v-model.number="form.submit_end" type="number" :min="minDay" :max="maxDay" :disabled="!canUpdate" />
                  <span class="require">{{ errors[0] || apiErrors.submit_end }}</span>
                </ValidationProvider>
              </CCol>
            </CRow>
            <p class="note">{{ $t("timesheet_submit_period.note", { min: minDay, max: maxDay }) }}</p>
            <CButton v-if="canUpdate" type="submit" color="primary">{{ $t("timesheet_submit_period.save") }}</CButton>
          </CForm>
        </ValidationObserver>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import api from "../../api/api";

// Same bounds as backend TimesheetSubmitPeriodRequest (max 28 avoids month overflow of setDay)
const MIN_DAY = 1;
const MAX_DAY = 28;
const UPDATE_PERMISSION = "timesheet-submit-period.update";

// Timesheet submit period settings (configs table, module Timesheet: submit_start / submit_end)
export default {
  name: "TimesheetSubmitPeriodSetting",
  // vee-validate components are not registered globally in this app
  components: { ValidationProvider, ValidationObserver },
  metaInfo() {
    return {
      title: this.$t("timesheet_submit_period.title"),
      titleTemplate: "%s | " + this.$t("page_title.head_title")
    };
  },
  data() {
    return {
      minDay: MIN_DAY,
      maxDay: MAX_DAY,
      // Saved values (used for description + period-change warning)
      current: { submit_start: "", submit_end: "" },
      form: { submit_start: "", submit_end: "" },
      apiErrors: {}
    };
  },
  computed: {
    ...mapGetters({ account: "auth/account" }),
    canUpdate() {
      return ((this.account && this.account.permissions) || []).includes(UPDATE_PERMISSION);
    },
    dayRules() {
      return `required|integer|min_value:${MIN_DAY}|max_value:${MAX_DAY}`;
    },
    orderError() {
      const { submit_start: start, submit_end: end } = this.form;
      return start !== "" && end !== "" && start > end ? this.$t("timesheet_submit_period.order_error") : "";
    }
  },
  watch: {
    // Drop stale server errors once the user edits the form
    form: {
      deep: true,
      handler() {
        this.apiErrors = {};
      }
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    ...mapActions("layout", ["setLoading"]),
    async fetchData() {
      this.setLoading(true);
      try {
        const response = await axios.get(api.TIMESHEET_SUBMIT_PERIOD);
        this.applyPeriod(response.data);
      } catch (e) {
        // Error toast already shown by axios interceptor
      } finally {
        this.setLoading(false);
      }
    },
    applyPeriod(period) {
      this.current = { ...period };
      this.form = { ...period };
    },
    // Today is inside [start, end] (same-month period, as TimeSheetHelper::submitable)
    isTodayInPeriod(period) {
      const today = moment().date();
      return today >= period.submit_start && today <= period.submit_end;
    },
    // Period change takes effect immediately (submit rights + editable/lock boundary via submit_end)
    confirmChange() {
      const key = this.isTodayInPeriod(this.current) !== this.isTodayInPeriod(this.form)
        ? "timesheet_submit_period.warning_submit_today"
        : "timesheet_submit_period.warning_period_change";
      // $confirm helper hides the cancel button, call bvModal directly to allow cancelling
      return this.$bvModal.msgBoxConfirm(this.$t(key), {
        title: this.$t("popup.label_popup_title"),
        okTitle: this.$t("popup.label_confirm_button"),
        cancelTitle: this.$t("popup.label_cancel_button"),
        okVariant: "warning",
        size: "sm",
        buttonSize: "sm",
        centered: true
      }).catch(() => false);
    },
    async submit() {
      if (this.orderError) return;
      const unchanged = this.form.submit_start === this.current.submit_start
        && this.form.submit_end === this.current.submit_end;
      if (!unchanged && !(await this.confirmChange())) return;
      this.apiErrors = {};
      this.setLoading(true);
      try {
        const response = await axios.post(api.TIMESHEET_SUBMIT_PERIOD, this.form);
        this.applyPeriod(response.data);
        this.$toast(this.$t("timesheet_submit_period.save_success"), null, 2000, "b-toaster-top-center", "success");
      } catch (error) {
        const messages = error.response && error.response.status === 422
          ? error.response.data.error.message : {};
        this.apiErrors = Object.keys(messages || {}).reduce(
          (acc, key) => ({ ...acc, [key]: messages[key][0] }), {}
        );
      } finally {
        this.setLoading(false);
      }
    }
  }
};
</script>

<style scoped>
.form-field {
  margin-bottom: 0.5rem;
}
label {
  font-weight: 600;
}
.require {
  color: red;
}
.description {
  font-weight: 600;
}
.note {
  color: #768192;
  font-style: italic;
}
</style>
